import { type FC, Fragment, Suspense, useEffect } from 'react';

import {
  Outlet as RouterOutlet,
  type OutletProps,
  ScrollRestoration,
  useNavigate,
} from 'react-router-dom';

import { MODAL_COMPONENTS_DICT } from '@/modals-dict';

import { type ModalsRegistrationDict } from '@/app/modals/types';
import { Modals } from '@/app/modals/ui/Modals';

import { useDaysStreakPageRedirect } from '@/pages/days-streak-page/hooks/use-day-streak-page-redirect';

import {
  PLAYCOIN_BOOSTER_BANNER_KEY,
  usePlaycoinBoostAlert,
  usePlaycoinBoostStickyBanner,
} from '@/widgets/boost';
import {
  useXPBoostStickyBanner,
  XP_BOOSTER_BANNER_KEY,
} from '@/widgets/boost/hooks/use-xp-boost-sticky-banner';
import { BottomBar } from '@/widgets/bottom-bar/BottomBar';
import { DevBanner } from '@/widgets/dev-banner/DevBanner';
import { useStartAppArgsManager } from '@/widgets/main-page/hooks/use-startapp-args-manager';
import { useNewFeatureAlert } from '@/widgets/new-feature-alert';
import { useXPModalNotification } from '@/widgets/xp-modal/hooks/use-xp-modal-notification';

import { PUSH_LOOTBOX_PARAM_KEY } from '@/shared/constants';
import { ErrorBoundaryWrapper } from '@/shared/hocs/withErrorBoundary/ErrorBoundary';
import { useTelegramBackButton } from '@/shared/hooks/telegram/use-telegram-back-button';
import { useTelegramSettingsButton } from '@/shared/hooks/telegram/use-telegram-settings-button';
import { useScrollToAnchor } from '@/shared/hooks/use-scroll-to-anchor';
import { useStartappArgs } from '@/shared/store/tg-store/tg-store';
import { Loader } from '@/shared/ui/loader';
import { StickyBanner } from '@/shared/ui/sticky-banner';

import { ROUTE_PATHS } from './types';

const RouterErrorBoundary = import.meta.env.DEV ? Fragment : ErrorBoundaryWrapper;

export const Outlet: FC<OutletProps & { hideNavbar?: boolean }> = ({
  hideNavbar,
  ...props
}) => {
  useStartAppArgsManager();
  useNewFeatureAlert();
  usePlaycoinBoostAlert();

  usePlaycoinBoostStickyBanner();
  useXPBoostStickyBanner();

  useTelegramBackButton();
  useTelegramSettingsButton();

  useXPModalNotification();

  useScrollToAnchor();

  const navigate = useNavigate();
  const startAppArgs = useStartappArgs();

  useEffect(() => {
    const handleScroll = () => {
      document.body.scrollTop = 0;
    };

    window.addEventListener('scroll', handleScroll, false);
    document.body.addEventListener('scroll', handleScroll, false);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Claim prize on app startup
    if (startAppArgs?.prize_id) {
      const url = `/${ROUTE_PATHS.LOOTBOX}/${startAppArgs.prize_id}?${PUSH_LOOTBOX_PARAM_KEY}=true`;

      navigate(url);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDaysStreakPageRedirect();

  return (
    <>
      <RouterErrorBoundary>
        <Suspense fallback={<Loader />}>
          <RouterOutlet {...props} />
        </Suspense>
      </RouterErrorBoundary>
      {!hideNavbar && <BottomBar />}
      <ScrollRestoration
        getKey={(location) =>
          [location.pathname, location.state?.scrollTo].filter(Boolean).join('_')
        }
        // Added via patch to handle not body scroll
        getScrollTarget={() => document.getElementById('root')}
      />

      <StickyBanner id={PLAYCOIN_BOOSTER_BANNER_KEY} />
      <StickyBanner id={XP_BOOSTER_BANNER_KEY} />
      <DevBanner />

      <Modals modals={MODAL_COMPONENTS_DICT as ModalsRegistrationDict} />
    </>
  );
};
